import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'

import { Seo } from '@/components/generals/seo'
import { SiteHeader } from '@/components/landing/site-header'
import { SiteFooter } from '@/components/landing/site-footer'
import { getSessionUser } from '@/lib/server/auth'

interface PrivacyProps {
    signedIn: boolean
}

const SECTIONS = [
    {
        title: 'What we keep',
        body: [
            'Your account: your name, email address, a securely scrambled copy of your password, your role and your time zone.',
            'Your classes: the classes you teach or joined, their schedules, the slides uploaded to them, the final whiteboard, live notes and recordings.',
            'Reminders: the notifications we sent you and, if you turned them on, the browser address we use to deliver notifications to your device.'
        ]
    },
    {
        title: 'Live video and sound',
        body: [
            'Video, sound and whiteboard strokes travel through our live video provider while a class runs. They are not stored unless the educator records the class, which every participant can see from the recording badge.',
            'Recordings and notes are only available to the educator and the learners registered for that class.'
        ]
    },
    {
        title: 'Live notes',
        body: [
            'When an educator turns on live notes, their browser converts speech to text using the speech service built into that browser. The resulting text is saved with the class.'
        ]
    },
    {
        title: 'Emails',
        body: [
            'We send class reminders and password reset emails. Reminder emails can be turned off at any time in your settings.',
            'Our emails record whether they were opened and which links were clicked, so we can tell whether reminders arrive. Password reset links are never tracked.'
        ]
    },
    {
        title: 'Cookies',
        body: [
            'We use one cookie to keep you signed in. We do not use advertising cookies.'
        ]
    },
    {
        title: 'Your choices',
        body: [
            'You can change your name, time zone and reminder settings in Settings. To delete your account and data, contact the person who runs this site.'
        ]
    }
]

const Privacy: FC<PrivacyProps> = ({ signedIn }): ReactNode => (
    <div className='flex min-h-screen flex-col'>
        <Seo title='Privacy' description='How Chalkline handles your account, classes, recordings, notes, emails and cookies.' />

        <SiteHeader signedIn={signedIn} showSections={false} />

        <main id='main' className='mx-auto w-full max-w-3xl flex-1 px-4 py-14 sm:px-6 sm:py-20'>
            <h1 className='text-4xl font-semibold sm:text-5xl'>
                Privacy
            </h1>

            <p className='mt-5 text-lg leading-relaxed text-muted-foreground'>
                A plain summary of what this site stores and why. We only keep what is needed to run your classes.
            </p>

            <div className='mt-12 flex flex-col gap-10'>
                {SECTIONS.map(section => (
                    <section key={section.title}>
                        <h2 className='text-2xl font-semibold'>
                            {section.title}
                        </h2>

                        <div className='mt-3 flex flex-col gap-3 leading-relaxed text-muted-foreground'>
                            {section.body.map(paragraph => (
                                <p key={paragraph}>
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </main>

        <SiteFooter />
    </div>
)

export const getServerSideProps: GetServerSideProps<PrivacyProps> = async ({ req, res }) => {
    const user = await getSessionUser(req, res)

    return { props: { signedIn: Boolean(user) } }
}

export default Privacy
